import {useMutation} from '@apollo/client';
import {Button} from '@dabafinance/react-components';
import {CREATE_PUBLIC_EQUITY} from 'graphql/mutations/assetManagement';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {handleClearEquityState} from 'redux/store/assetClass';
import {currencySymbols} from 'utils/mock';
import {buildBaseInstrumentInput} from '../modules/buildBaseInstrumentInput';

// Drop a value object entirely when the operator left every field on it blank,
// rather than sending an object of nulls.
const compact = object => {
  const entries = Object.entries(object).filter(
    ([, value]) => value !== undefined && value !== null && value !== '',
  );
  return entries.length ? Object.fromEntries(entries) : undefined;
};

const asDate = value => (value ? moment(value).format() : undefined);

export const CreatePublicEquityFinishModal = ({handleClose}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {assetId: assetKey} = useParams();
  const draft = useSelector(state => state?.assetClass);

  const [createPublicEquity, {loading}] = useMutation(CREATE_PUBLIC_EQUITY);

  const handleCreateEquity = () => {
    const instrument = draft?.equityInstruments ?? {};
    const {listedAt, dividend, latestQuote, ticker, isin} = draft?.equity ?? {};

    // `sharesOutstanding`, `keyMetrics` and `ranking` are readable on the type
    // but absent from PublicEquityInput, so there is nothing to send for them.
    const cleanedDividend = compact({
      ...dividend,
      exDate: asDate(dividend?.exDate),
      nextDate: asDate(dividend?.nextDate),
      lastDate: asDate(dividend?.lastDate),
    });

    createPublicEquity({
      variables: {
        input: {
          ...(listedAt && {listedAt: asDate(listedAt)}),
          ...(cleanedDividend && {dividend: cleanedDividend}),
          ...(compact(latestQuote ?? {}) && {
            latestQuote: compact(latestQuote ?? {}),
          }),
          // `exchange` is offered on PublicEquityInput as well as on the base
          // input; send it on both so whichever the resolver reads is set.
          ...(instrument?.exchange && {exchange: instrument.exchange}),
          baseFinancialInstrumentInput: buildBaseInstrumentInput({
            key: 'PUBLIC_EQUITY',
            assetKey,
            instrument,
            ticker: ticker ?? instrument?.ticker,
            isin: isin ?? instrument?.isin,
            currency: currencySymbols[instrument?.currency],
          }),
        },
      },
    })
      .then(({data, errors}) => {
        const result = data?.createPublicEquity;
        if (result?.__typename === 'Error') {
          toast.error(result.message);
        } else if (errors) {
          toast.error(errors[0].message);
        } else {
          toast.success('Created Successfully');
          dispatch(handleClearEquityState());
          navigate(`/dashboard/asset-management/class/${assetKey}/instruments`);
        }
      })
      .catch(error => toast.error(error.message));
  };

  return (
    <div className="confirm-modal">
      <div>
        <h1>Create Public Equity</h1>
        <p>
          Please make sure the details are correctly inputted before proceeding
        </p>
      </div>
      <div className="buttons">
        <Button
          disabled={loading}
          label={!loading ? 'Create' : 'Loading...'}
          onClick={handleCreateEquity}
        />
        <Button type="secondary" label="No" onClick={handleClose} />
      </div>
    </div>
  );
};

export default CreatePublicEquityFinishModal;
